import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calculator, Sparkles, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

type Recommendation = {
  id: "basic" | "entry" | "medium" | "custom";
  name: string;
  price: string;
  period: string;
  reason: string;
};

interface QuoteCalculatorProps {
  onSelect: (packageName: string) => void;
}

const parseCurrency = (value: string): number => {
  const digits = value.replace(/[^0-9]/g, "");
  if (!digits) return 0;
  const num = Number(digits);
  return Number.isFinite(num) ? num : 0;
};

const formatCurrency = (value: number): string =>
  value === 0 ? "" : `R${value.toLocaleString("en-ZA")}`;

export const QuoteCalculator = ({ onSelect }: QuoteCalculatorProps) => {
  const [turnoverInput, setTurnoverInput] = useState("");
  const [employees, setEmployees] = useState<number>(0);
  const [vatRegistered, setVatRegistered] = useState<"yes" | "no" | "">("");
  const [needsPayroll, setNeedsPayroll] = useState<"yes" | "no" | "">("");
  const [showResult, setShowResult] = useState(false);

  const turnover = useMemo(() => parseCurrency(turnoverInput), [turnoverInput]);

  const recommendation = useMemo<Recommendation | null>(() => {
    if (vatRegistered === "" || needsPayroll === "") return null;

    // Custom quote scenarios
    if (turnover > 12_000_000 || employees > 10) {
      return {
        id: "custom",
        name: "Custom Quote",
        price: "Tailored",
        period: "quote",
        reason:
          turnover > 12_000_000
            ? "Your turnover exceeds R12m — we'll tailor a package for your scale."
            : "You have more than 10 employees — payroll requires a custom setup.",
      };
    }

    // Medium tier
    if (
      turnover > 2_300_000 ||
      vatRegistered === "yes" ||
      employees > 5 ||
      (needsPayroll === "yes" && employees > 5)
    ) {
      const reasons: string[] = [];
      if (turnover > 2_300_000) reasons.push("turnover above R2.3m");
      if (vatRegistered === "yes") reasons.push("VAT registered");
      if (employees > 5) reasons.push(`${employees} employees`);
      return {
        id: "medium",
        name: "Medium Level Package",
        price: "R4,500",
        period: "p/m",
        reason: `Best fit because you're ${reasons.join(", ")}.`,
      };
    }

    // Basic tier — dormant / nil turnover
    if (turnover === 0 && employees === 0 && needsPayroll === "no") {
      return {
        id: "basic",
        name: "Basic Package",
        price: "R300",
        period: "p/m",
        reason: "Nil turnover, no employees — covers your annual compliance only.",
      };
    }

    // Entry tier
    return {
      id: "entry",
      name: "Entry Level Package",
      price: "R2,500",
      period: "p/m",
      reason: `Turnover under R2.3m${employees > 0 ? ` with ${employees} employee${employees === 1 ? "" : "s"}` : ""} — the sweet spot for growing SMEs.`,
    };
  }, [turnover, employees, vatRegistered, needsPayroll]);

  const canCalculate = vatRegistered !== "" && needsPayroll !== "";

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    if (canCalculate) setShowResult(true);
  };

  const handleReset = () => {
    setTurnoverInput("");
    setEmployees(0);
    setVatRegistered("");
    setNeedsPayroll("");
    setShowResult(false);
  };

  return (
    <section className="py-16 lg:py-20 section-light">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              Instant Quote
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-3">
              Not sure which package fits?
            </h2>
            <p className="text-muted-foreground text-lg">
              Answer 4 quick questions and we'll recommend the right plan for your business.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-6 items-start">
            {/* Form */}
            <form
              onSubmit={handleCalculate}
              className="lg:col-span-3 bg-card rounded-xl border border-border p-6 md:p-8 shadow-soft space-y-6"
            >
              <div className="flex items-center gap-2 pb-4 border-b border-border">
                <Calculator className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-lg font-semibold">Your business details</h3>
              </div>

              <div className="space-y-2">
                <Label htmlFor="turnover">Annual turnover (ZAR)</Label>
                <Input
                  id="turnover"
                  inputMode="numeric"
                  placeholder="e.g. R1,500,000"
                  value={formatCurrency(turnover)}
                  onChange={(e) => setTurnoverInput(e.target.value)}
                  maxLength={20}
                />
                <p className="text-xs text-muted-foreground">
                  Enter 0 if dormant / no trading activity.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="employees">Number of employees</Label>
                <Input
                  id="employees"
                  type="number"
                  min={0}
                  max={999}
                  placeholder="0"
                  value={employees === 0 ? "" : employees}
                  onChange={(e) => {
                    const v = Number(e.target.value.replace(/[^0-9]/g, ""));
                    setEmployees(Number.isFinite(v) ? Math.min(v, 999) : 0);
                  }}
                />
              </div>

              <div className="space-y-3">
                <Label>Are you VAT registered?</Label>
                <RadioGroup
                  value={vatRegistered}
                  onValueChange={(v) => setVatRegistered(v as "yes" | "no")}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="vat-yes" />
                    <Label htmlFor="vat-yes" className="font-normal cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="vat-no" />
                    <Label htmlFor="vat-no" className="font-normal cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label>Do you need payroll processing?</Label>
                <RadioGroup
                  value={needsPayroll}
                  onValueChange={(v) => setNeedsPayroll(v as "yes" | "no")}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="payroll-yes" />
                    <Label htmlFor="payroll-yes" className="font-normal cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="payroll-no" />
                    <Label htmlFor="payroll-no" className="font-normal cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="submit" className="btn-primary flex-1" disabled={!canCalculate}>
                  Calculate my package
                </Button>
                {showResult && (
                  <Button type="button" variant="outline" onClick={handleReset}>
                    Reset
                  </Button>
                )}
              </div>
            </form>

            {/* Result */}
            <div className="lg:col-span-2 lg:sticky lg:top-24">
              <div
                className={cn(
                  "rounded-xl border p-6 md:p-8 transition-all min-h-[320px] flex flex-col",
                  showResult && recommendation
                    ? "bg-primary/5 border-primary/30 shadow-elegant"
                    : "bg-muted/30 border-dashed border-border"
                )}
              >
                {showResult && recommendation ? (
                  <>
                    <p className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">
                      Recommended for you
                    </p>
                    <h3 className="font-heading text-2xl font-bold text-foreground mb-4">
                      {recommendation.name}
                    </h3>
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="price-amount">{recommendation.price}</span>
                      {recommendation.period && (
                        <span className="price-period">{recommendation.period}</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-6 flex-1">
                      {recommendation.reason}
                    </p>
                    <Button
                      className="btn-primary w-full gap-2"
                      onClick={() => onSelect(recommendation.name)}
                    >
                      Enquire about this package
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                    <p className="text-xs text-muted-foreground mt-3 text-center">
                      Estimate only — final quote confirmed after review.
                    </p>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center flex-1 text-muted-foreground">
                    <Calculator className="w-12 h-12 mb-3 opacity-40" />
                    <p className="text-sm">
                      Fill in your details to see your recommended package.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
